import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
// import { CourierDto, CourierResolverDto, CouriersApi, ResolversApi } from '../shared/sdk';
import { apiConfig } from '../shared/api-config';
import {
  CourierApi,
  CourierResolverApi,
  CourierResolver,
  Courier,
  PartnerLinkApi,
  StoreLocation,
  LegalDocument,
} from '@shared/sdk-v1';
import { PayloadAction } from '@reduxjs/toolkit';
import { CourierSettings } from '@shared/sdk-v1';
import { buildCourierManifest } from '../utils/pwaManifest';

/**
 * Generate a padded, square, maskable PNG (data URL) from the courier logo so
 * Android uses the courier logo for the home-screen (adaptive) icon. The logo is
 * drawn within the ~80% maskable safe zone on a white background. Returns null if
 * the image can't be loaded/read onto a canvas (e.g. CORS) — callers fall back to
 * the static OX maskable icons so installability is never blocked.
 */
const renderCourierLogoPng = (
  imageUrl: string,
  { size, scale, background }: { size: number; scale: number; background: string | null }
): Promise<string | null> =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          if (background) {
            ctx.fillStyle = background;
            ctx.fillRect(0, 0, size, size);
          }
          // Fit the logo (preserving aspect ratio) into `scale` of the canvas, centered.
          const box = Math.round(size * scale);
          const fit = Math.min(box / img.width, box / img.height);
          const w = img.width * fit;
          const h = img.height * fit;
          ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = imageUrl;
    } catch {
      resolve(null);
    }
  });

/**
 * A padded, square, maskable PNG (data URL) of the courier logo for the PWA
 * home-screen (adaptive) icon. The logo sits within the ~80% maskable safe zone
 * on a white background. Returns null if the image can't be read onto a canvas
 * (e.g. CORS) — callers fall back to the static OX maskable icons.
 */
const generateMaskableIcon = (imageUrl: string): Promise<string | null> =>
  renderCourierLogoPng(imageUrl, { size: 512, scale: 0.6, background: '#ffffff' });

/**
 * A PNG (data URL) of the courier logo for the browser-tab favicon. PNG is used
 * because Chrome doesn't reliably render WebP favicons (many courier logos are
 * WebP), so linking the raw logo leaves the tab showing the OX favicon.ico.
 */
const generateFaviconPng = (imageUrl: string): Promise<string | null> =>
  renderCourierLogoPng(imageUrl, { size: 96, scale: 1, background: null });
import { ThemeSettingApi } from '@shared/sdk-v1';
import { ThemeSetting } from '@shared/sdk-v1';
import { RootState } from '../store';
import { hasSecret } from '../utils/courierSecrets';

/**
 * Is this courier somebody's sub-courier?
 *
 * A courier shipping through an OX-native warehouse (Kajay and friends) is
 * joined to it by a PartnerLink, not by a vendor warehouse API key, so this is
 * the only signal that Warehouse Management has anything to show them.
 *
 * It is resolved as part of the courier bootstrap rather than in AdminShell
 * (which already probes PartnerLinks) because AdminShell mounts *inside* the
 * route AdminProtectedRoute guards — by the time it could answer, the guard has
 * already bounced the user to /unauthorized. App.tsx blocks the router on
 * fetchCourier, so resolving it here removes the race entirely.
 *
 * `/PartnerLinks/is-partner-linked` is `$authenticated ALLOW`, unlike
 * `getPartnerLinks`, so Cashiers and Warehouse Administrators get an answer too.
 *
 * Never throws: the whole admin is blocked on the calling thunk.
 */
const resolveIsPartnerLinked = async (
  courierId?: number | string
): Promise<PartnerLinkProbe> => {
  if (!courierId) return { isPartnerLinked: false, resolved: false };
  // The bootstrap runs before anyone signs in, and this endpoint is
  // $authenticated-only — so calling it while logged out can only ever 401.
  // There is nothing useful to learn from that answer, and the round trip is
  // on the critical path of every logged-out page load. usePartnerLinkRefresh
  // re-resolves it the moment the user authenticates.
  if (!localStorage.getItem('accessToken')) {
    return { isPartnerLinked: false, resolved: false };
  }
  try {
    const partnerLinkApi = new PartnerLinkApi(apiConfig());
    const result = await partnerLinkApi.partnerLinkIsPartnerLinked({
      courierId: courierId.toString(),
    });
    return { isPartnerLinked: !!result?.isPartnerLinked, resolved: true };
  } catch (error) {
    console.error('Failed to resolve partner-linked status:', error);
    return { isPartnerLinked: false, resolved: false };
  }
};

/**
 * The partner-link answer, plus whether the probe actually ran. `resolved:
 * false` means nobody asked the API (no courier, or nobody signed in yet) —
 * the `isPartnerLinked: false` alongside it is a placeholder, not an answer,
 * and usePartnerLinkRefresh uses that to know it still has to ask.
 */
type PartnerLinkProbe = { isPartnerLinked: boolean; resolved: boolean };

/** The courier, plus the partner-link answer resolved alongside it. */
type CourierWithPartnerLink = Courier & {
  isPartnerLinked?: boolean;
  partnerLinkResolved?: boolean;
};

export const fetchCourier = createAsyncThunk(
  'courier/fetchCourier',
  async () => {
    console.log('fetchCourier');
    const resolverApi = new CourierResolverApi(apiConfig());
    const courierApi = new CourierApi(apiConfig());

    const host = window.location.host;
    const resolverRecord: CourierResolver =
      await resolverApi.courierResolverFindOne({
        filter: JSON.stringify({
          where: {
            or: [
              { adminUrl: host },
              { adminUrl: host.replace('admin-v2', 'admin') },
              { adminUrl: host.replace('admin', 'admin-v2') },
              { adminUrl: host.replace('admin-v1', 'admin') },
            ],
          },
          fields: {
            courierId: true,
          },
        }),
      });
    // console.log(`Resolved to courier id ${resolverRecord?.courierId}`);

    if (!resolverRecord?.courierId) {
      throw new Error('No courier found');
    }

    const courier = await courierApi.courierFindById({
      id: resolverRecord?.courierId?.toString(),
      filter: JSON.stringify({
        fields: {
          id: true,
          isActive: true,
          realm: true,
          name: true,
          hostingUrl: true,
          imageUrl: true,
          mailboxPaddingLength: true,
          email: true,
          webUrl: true,
          subscriptionStatus: true,
          stripeCustomerId: true,
          planId: true,
          onboardingStatus: true,
        },
        include: [
          'plan',
          'storeLocations',
          'themeSetting',
          {
            relation: 'legalDocument',
            scope: {
              fields: {
                id: true,
                termsOfService: true,
                privacyPolicy: true,
                lastUpdatedTerms: true,
                lastUpdatedPrivacy: true,
              },
            },
          },
          {
            relation: 'courierSetting',
            scope: {
              fields: {
                id: true,
                courierId: true,
                prefix: true,
                isSpeedzMasterV2ApiEnabled: true,
                isJaShippersApiEnabled: true,
                jaShippersApiKey: true,
                jaShippersApiBaseUrl: true,
                isDeliveryEnabled: true,
                deliveryApiKey: true,
                isLogisApiEnabled: true,
                logisApiAccessToken: true,
                logisApiBaseUrl: true,
                processingFee1: true,
                processingFee2: true,
                processingFee3: true,
                processingFee4: true,
                processingFee1Type: true,
                processingFee2Type: true,
                processingFee3Type: true,
                processingFee4Type: true,
                processingFeeWeightLimit: true,
                weightPrice: true,
                expressWeightPrice: true,
                isFygaroApiEnabled: true,
                fygaroApiBaseUrl: true,
                fygaroServiceChargePercent: true,
                stripeServiceChargePercent: true,
                whatsappBotEnabled: true,
                isWhatsappGatewayEnabled: true,
                statusAlertOptions: true,
                additionalShippingAddresses: true,
                statusAlertMode: true,
                whatsappServerUrl: true,
                isShipCorpApiEnabled: true,
                shipCorpApiAccessToken: true,
                shipCorpApiBaseUrl: true,
                isScanInEnabled: true,
                isSmartScanEnabled: true,
                isSmartScanTwoStepEnabled: true,
                landingPageVersion: true,
                isShipBizApiEnabled: true,
                shipBizApiKey: true,
                skgApiKey: true,
                shipBizApiBaseUrl: true,
                isCargoJaApiEnabled: true,
                isFarin2YaadApiEnabled: true,
                isAtoraApiEnabled: true,
                atoraApiBaseUrl: true,
                useWaApi: true,
                isSkgApiEnabled: true,
                barrelPrice: true,
                pricePerCubicFoot: true,
                insuranceFee: true,
                missingCourierCodeFee: true,
                isWarehouseApiEnabled: true,
                scanBonusEnabled: true,
                scanBonusDailyTarget: true,
                scanBonusPerPackage: true,
                scanBonusPerPackageWithContents: true,
                scanBonusMode: true,
                scanBonusDeductionPerPackage: true,
                isScanInCourierLockEnabled: true,
                manifestCreationMode: true,
                isPushNotificationEnabled: true,
                isPasskeyEnabled: true,
                fcmServerKey: true,
                pushNotificationStatusOptions: true,
                isHnmApiEnabled: true,
                // OnePlus API fields
                isOnePlusApiEnabled: true,
                onePlusApiKey: true,
                // SSN Ship Courier Panel fields
                isSsnApiEnabled: true,
                ssnApiBaseUrl: true,
                ssnClientId: true,
                ssnLastCustomerSyncAt: true,
                // SCC Warehouse System fields
                isSccApiEnabled: true,
                sccApiBaseUrl: true,
                sccLastCustomerSyncAt: true,
                // TR Global Logistics fields. Secrets are deliberately absent —
                // they come back encrypted and WarehouseConfiguration treats a
                // stored secret as "protected" rather than loading it.
                isTrGlobalApiEnabled: true,
                trGlobalApiBaseUrl: true,
                trGlobalLastCustomerSyncAt: true,
                // App addon fields
                isAndroidAppEnabled: true,
                androidAppSubscriptionItemId: true,
                isIosAppEnabled: true,
                isMobileAdminEnabled: true,
                iosAppSubscriptionItemId: true,
                // Settings form fields
                contactInformation: true,
                socialMedia: true,
                storeAddress: true,
                isMultiLocationEnabled: true,
                invoiceNotification: true,
                // Shipping rates fields
                useJmd: true,
                currency: true,
                isCashDenominationEnabled: true,
                showDualCurrency: true,
                exchangeRate: true,
                businessExchangeRate: true,
                gct: true,
                isLateFeeEnabled: true,
                lateFeeAmount: true,
                lateFeeStartDate: true,
                lateFeeDays: true,
                notifyBeforeDays: true,
                courierStartPrice: true,
                courierAdditionalLbPrice: true,
                staffWeightPrice: true,
                staffExpressWeightPrice: true,
                staffStartPrice: true,
                staffAdditionalLbPrice: true,
                staffExchangeRate: true,
                useHalfPound: true,
                halfPoundPrice: true,
                fixedCustomRate: true,
                weightRoundingMode: true,
                // Invoice template
                invoiceTemplate: true,
                // Invoice settings
                invoiceGenerationMode: true,
                // HR & Payroll settings
                attendanceEnabled: true,
                payrollEnabled: true,
                payPeriodType: true,
                payPeriodStartDay: true,
                overtimeThresholdWeekly: true,
                overtimeMultiplier: true,
                requireClockInLocation: true,
                // Customer signup settings
                requireVerification: true,
                requireTrnOnSignup: true,
                requireAddressOnSignup: true,
                isReferredByEnabled: true,
                isReferralSourceEnabled: true,
                referralSourceOptions: true,
                // Rewards programme. Both gate real behaviour: pointsEnabled
                // decides whether the POS can take points as a tender (see
                // usePos -> pointsRedemptionEnabled) and the API enforces the
                // same flag in Invoice.checkout. Omitting them here left them
                // undefined and made points unredeemable at every till.
                pointsEnabled: true,
                referralEnabled: true,
                // Pre-alerts
                isPreAlertSupported: true,
                // Receipt printer settings
                isReceiptPrinterEnabled: true,
                receiptPrinterIp: true,
                receiptPrinterPort: true,
                // Turnstile CAPTCHA
                turnstileSiteKey: true,
                // Shop features
                isLocalDeliveryEnabled: true,
                isShopForMeEnabled: true,
                isShopEnabled: true,
                isBlogEnabled: true,
                isPackageRequestsEnabled: true,
                isCalculatorEnabled: true,
                shopPurchasingFeeType: true,
                shopPurchasingFeeAmount: true,
                shopPurchasingFeeThreshold: true,
                shopPurchasingFee2Type: true,
                shopPurchasingFee2Amount: true,
                isFulfillmentEnabled: true,
                isCustomerChatEnabled: true,
                isReviewsEnabled: true,
                isGalleryEnabled: true,
                isCustomsCalculatorEnabled: true,
                isExpenseTrackerEnabled: true,
                // Custom package statuses
                useCustomStatuses: true,
                // Timezone (IANA string, e.g. 'America/Jamaica', 'America/New_York')
                timezone: true,
                // Partner contracts + KYC — must match the fetchCourierForImpersonation
                // list below, or the partner requirements banner/modal never appear.
                isPartnerKycRequired: true,
                partnerKycGraceDays: true,
                partnerContractDefaultExpiryDays: true,
              },
            },
          },
          {
            relation: 'courierBills',
            scope: {
              fields: {
                id: true,
                status: true,
                dateDue: true,
              },
              where: {
                status: 'Unpaid',
              },
            },
          },{
            relation: 'wipayCredentials',
            scope: {
              fields: {
                isWipayApiEnabled: true,
                wipayCurrency: true,
                wipayApiBaseUrl: true,
                wipayFeeStructure: true,
                wipayEnvironment: true,
                wipayResponseUrl: true,
              },
            },
          },
          {
            relation: 'stripeCredentials',
            scope: {
              fields: {
                isStripeApiEnabled: true,
                restrictedKey: true,
              },
            },
          }
        ],
      }),
    });
    // console.log('courier', courier);

    console.log('settings', courier?.courierSetting);

    const features = {
      isPushNotificationEnabled: courier?.courierSetting?.isPushNotificationEnabled ?? false,
      hasFcmServerKey: hasSecret(courier?.courierSetting, 'fcmServerKey'),
      pushNotificationStatusOptions: courier?.courierSetting?.pushNotificationStatusOptions ?? [],
    };

    console.log('features', features);

    const partnerLink = await resolveIsPartnerLinked(courier?.id);

    return {
      ...courier,
      isPartnerLinked: partnerLink.isPartnerLinked,
      partnerLinkResolved: partnerLink.resolved,
    } as CourierWithPartnerLink;
  }
);

export const fetchCourierStoreLocations = createAsyncThunk(
  'courier/fetchStoreLocations',
  async (courierId: string) => {
    const courierApi = new CourierApi(apiConfig());
    const response = await courierApi.courierPrototypeGetStoreLocations({
      id: courierId,
    });
    return response;
  }
);

export const fetchCourierThemeSettings = createAsyncThunk(
  'courier/fetchThemeSettings',
  async (courierId: string) => {
    const themeSettingApi = new ThemeSettingApi(apiConfig());
    const filter = JSON.stringify({ where: { courierId: Number(courierId) } });
    const response = await themeSettingApi.themeSettingFind({ filter });
    return response[0] || null;
  }
);

/**
 * Re-resolves the partner-link answer once the user is authenticated.
 *
 * The bootstrap in fetchCourier runs while the admin is still logged out (the
 * router is blocked on it), and the probe is $authenticated-only, so a fresh
 * browser can never learn the answer there. Login is a client-side navigate()
 * — App never remounts and fetchCourier never runs again — so without this the
 * flag would stay false for the whole session and a partner-linked courier
 * would lose Warehouse Management.
 *
 * Unlike resolveIsPartnerLinked this is allowed to reject: nothing is blocked
 * on it, and a transient failure must leave the existing answer alone rather
 * than revoking access.
 */
export const refreshIsPartnerLinked = createAsyncThunk(
  'courier/refreshIsPartnerLinked',
  async (courierId: string) => {
    const partnerLinkApi = new PartnerLinkApi(apiConfig());
    const result = await partnerLinkApi.partnerLinkIsPartnerLinked({
      courierId,
    });
    return !!result?.isPartnerLinked;
  }
);

export const fetchCourierForImpersonation = createAsyncThunk(
  'courier/fetchCourierForImpersonation',
  async (courierId: string) => {
    const courierApi = new CourierApi(apiConfig());

    const courier = await courierApi.courierFindById({
      id: courierId,
      filter: JSON.stringify({
        fields: {
          id: true,
          isActive: true,
          realm: true,
          name: true,
          imageUrl: true,
          hostingUrl: true,
          mailboxPaddingLength: true,
          email: true,
          webUrl: true,
          subscriptionStatus: true,
          stripeCustomerId: true,
          planId: true,

        },
        include: [
          'plan',
          'storeLocations',
          'themeSetting',
          {
            relation: 'legalDocument',
            scope: {
              fields: {
                id: true,
                termsOfService: true,
                privacyPolicy: true,
                lastUpdatedTerms: true,
                lastUpdatedPrivacy: true,
              },
            },
          },
          {
            relation: 'courierSetting',
            scope: {
              fields: {
                id: true,
                courierId: true,
                prefix: true,
                isSpeedzMasterV2ApiEnabled: true,
                isJaShippersApiEnabled: true,
                jaShippersApiKey: true,
                jaShippersApiBaseUrl: true,
                isDeliveryEnabled: true,
                deliveryApiKey: true,
                isLogisApiEnabled: true,
                logisApiAccessToken: true,
                logisApiBaseUrl: true,
                processingFee1: true,
                processingFee2: true,
                processingFee3: true,
                processingFee4: true,
                processingFee1Type: true,
                processingFee2Type: true,
                processingFee3Type: true,
                processingFee4Type: true,
                processingFeeWeightLimit: true,
                weightPrice: true,
                expressWeightPrice: true,
                isFygaroApiEnabled: true,
                fygaroApiBaseUrl: true,
                fygaroServiceChargePercent: true,
                stripeServiceChargePercent: true,
                whatsappBotEnabled: true,
                isWhatsappGatewayEnabled: true,
                statusAlertOptions: true,
                additionalShippingAddresses: true,
                statusAlertMode: true,
                whatsappServerUrl: true,
                isShipCorpApiEnabled: true,
                shipCorpApiAccessToken: true,
                shipCorpApiBaseUrl: true,
                isScanInEnabled: true,
                isSmartScanEnabled: true,
                isSmartScanTwoStepEnabled: true,
                landingPageVersion: true,
                isShipBizApiEnabled: true,
                shipBizApiKey: true,
                skgApiKey: true,
                shipBizApiBaseUrl: true,
                isCargoJaApiEnabled: true,
                isFarin2YaadApiEnabled: true,
                isAtoraApiEnabled: true,
                atoraApiBaseUrl: true,
                useWaApi: true,
                isSkgApiEnabled: true,
                barrelPrice: true,
                pricePerCubicFoot: true,
                insuranceFee: true,
                missingCourierCodeFee: true,
                isWarehouseApiEnabled: true,
                scanBonusEnabled: true,
                scanBonusDailyTarget: true,
                scanBonusPerPackage: true,
                scanBonusPerPackageWithContents: true,
                scanBonusMode: true,
                scanBonusDeductionPerPackage: true,
                isScanInCourierLockEnabled: true,
                manifestCreationMode: true,
                isPushNotificationEnabled: true,
                isPasskeyEnabled: true,
                fcmServerKey: true,
                pushNotificationStatusOptions: true,
                isHnmApiEnabled: true,
                // OnePlus API fields
                isOnePlusApiEnabled: true,
                onePlusApiKey: true,
                // SSN Ship Courier Panel fields
                isSsnApiEnabled: true,
                ssnApiBaseUrl: true,
                ssnClientId: true,
                ssnLastCustomerSyncAt: true,
                // SCC Warehouse System fields
                isSccApiEnabled: true,
                sccApiBaseUrl: true,
                sccLastCustomerSyncAt: true,
                // TR Global Logistics fields. Secrets are deliberately absent —
                // they come back encrypted and WarehouseConfiguration treats a
                // stored secret as "protected" rather than loading it.
                isTrGlobalApiEnabled: true,
                trGlobalApiBaseUrl: true,
                trGlobalLastCustomerSyncAt: true,
                // App addon fields
                isAndroidAppEnabled: true,
                androidAppSubscriptionItemId: true,
                isIosAppEnabled: true,
                isMobileAdminEnabled: true,
                iosAppSubscriptionItemId: true,
                // Settings form fields
                contactInformation: true,
                storeAddress: true,
                socialMedia: true,
                isMultiLocationEnabled: true,
                invoiceNotification: true,
                // Shipping rates fields
                useJmd: true,
                currency: true,
                isCashDenominationEnabled: true,
                showDualCurrency: true,
                exchangeRate: true,
                businessExchangeRate: true,
                gct: true,
                isLateFeeEnabled: true,
                lateFeeAmount: true,
                lateFeeStartDate: true,
                lateFeeDays: true,
                notifyBeforeDays: true,
                courierStartPrice: true,
                courierAdditionalLbPrice: true,
                staffWeightPrice: true,
                staffExpressWeightPrice: true,
                staffStartPrice: true,
                staffAdditionalLbPrice: true,
                staffExchangeRate: true,
                useHalfPound: true,
                halfPoundPrice: true,
                fixedCustomRate: true,
                weightRoundingMode: true,
                // Invoice template
                invoiceTemplate: true,
                // Invoice settings
                invoiceGenerationMode: true,
                // HR & Payroll settings
                attendanceEnabled: true,
                payrollEnabled: true,
                payPeriodType: true,
                payPeriodStartDay: true,
                overtimeThresholdWeekly: true,
                overtimeMultiplier: true,
                requireClockInLocation: true,
                // Customer signup settings
                requireVerification: true,
                requireTrnOnSignup: true,
                requireAddressOnSignup: true,
                isReferredByEnabled: true,
                isReferralSourceEnabled: true,
                referralSourceOptions: true,
                // Rewards programme — must match the fetchCourier list above,
                // or points redemption breaks while impersonating a courier.
                pointsEnabled: true,
                referralEnabled: true,
                // Pre-alerts
                isPreAlertSupported: true,
                // Receipt printer settings
                isReceiptPrinterEnabled: true,
                receiptPrinterIp: true,
                receiptPrinterPort: true,
                // Turnstile CAPTCHA
                turnstileSiteKey: true,
                // Shop features
                isLocalDeliveryEnabled: true,
                isShopForMeEnabled: true,
                isShopEnabled: true,
                isBlogEnabled: true,
                isPackageRequestsEnabled: true,
                isCalculatorEnabled: true,
                shopPurchasingFeeType: true,
                shopPurchasingFeeAmount: true,
                shopPurchasingFeeThreshold: true,
                shopPurchasingFee2Type: true,
                shopPurchasingFee2Amount: true,
                isFulfillmentEnabled: true,
                isCustomerChatEnabled: true,
                isReviewsEnabled: true,
                isGalleryEnabled: true,
                isCustomsCalculatorEnabled: true,
                isExpenseTrackerEnabled: true,
                // Custom package statuses
                useCustomStatuses: true,
                // Timezone (IANA string, e.g. 'America/Jamaica', 'America/New_York')
                timezone: true,
                // Partner contracts + KYC — must match the fetchCourier list above,
                // or the partner requirements banner/modal never appear while
                // impersonating a courier.
                isPartnerKycRequired: true,
                partnerKycGraceDays: true,
                partnerContractDefaultExpiryDays: true,
              },
            },
          },
          {
            relation: 'courierBills',
            scope: {
              fields: {
                id: true,
                status: true,
                dateDue: true,
              },
              where: {
                status: 'Unpaid',
              },
            },
          },
          {
            relation: 'wipayCredentials',
            scope: {
              fields: {
                isWipayApiEnabled: true,
                wipayApiKey: true,
                wipayAccountNumber: true,
                wipayCountryCode: true,
                wipayCurrency: true,
                wipayApiBaseUrl: true,
                wipayFeeStructure: true,
                wipayEnvironment: true,
                wipayResponseUrl: true,
              },
            },
          },
          {
            relation: 'stripeCredentials',
            scope: {
              fields: {
                isStripeApiEnabled: true,
                restrictedKey: true,
              },
            },
          }
        ],
      }),
    });

    const partnerLink = await resolveIsPartnerLinked(courierId);

    return {
      ...courier,
      isPartnerLinked: partnerLink.isPartnerLinked,
      partnerLinkResolved: partnerLink.resolved,
    } as CourierWithPartnerLink;
  }
);

interface CourierState {
  isLoading: boolean;
  courier?: Courier;
  isBillDue?: boolean;
  error?: string;
  storeLocations: StoreLocation[];
  /**
   * True when this courier is an active sub-courier of at least one warehouse
   * tenant (a PartnerLink). Resolved with the courier so route guards and the
   * sidebar can read it synchronously — see resolveIsPartnerLinked.
   */
  isPartnerLinked: boolean;
  /**
   * Whether isPartnerLinked above is an actual answer from the API rather than
   * the default. False after a logged-out bootstrap, which is how
   * usePartnerLinkRefresh knows to ask again once the user signs in.
   */
  partnerLinkResolved: boolean;
  // Impersonation state
  isImpersonating: boolean;
  originalCourier?: Courier;
  impersonatedCourier?: Courier;
}

const initialState: CourierState = {
  isLoading: true,
  courier: undefined,
  error: undefined,
  storeLocations: [],
  isPartnerLinked: false,
  partnerLinkResolved: false,
  // Impersonation initial state
  isImpersonating: false,
  originalCourier: undefined,
  impersonatedCourier: undefined,
};

export const courierSlice = createSlice({
  name: 'courier',
  initialState,
  reducers: {
    updateCourier: (state, action) => {
      state.courier = action.payload;
      state.error = undefined;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
      state.error = undefined;
    },
    setIsPartnerLinked: (state, action: PayloadAction<boolean>) => {
      state.isPartnerLinked = action.payload;
    },
    updateCourierSettings: (state, action: PayloadAction<CourierSettings>) => {
      if (state.courier) {
        state.courier.courierSetting = action.payload;
      }
    },
    updateStoreLocations: (state, action: PayloadAction<StoreLocation[]>) => {
      state.storeLocations = action.payload;
    },
    updateThemeSettings: (state, action: PayloadAction<ThemeSetting>) => {
      if (state.courier) {
        state.courier.themeSetting = action.payload;
      }
    },
    // Keep courier.legalDocument fresh after a create/update so a remount
    // re-seeds the real terms/privacy (and the hook uses the update branch,
    // not the create branch) instead of an empty editor. Only touches the
    // relation, leaving the rest of the courier intact.
    updateLegalDocument: (state, action: PayloadAction<Partial<LegalDocument>>) => {
      if (state.courier) {
        state.courier.legalDocument = action.payload;
      }
    },
    // Impersonation Actions
    startImpersonation: (state, action: PayloadAction<Courier>) => {
      // Only allow impersonation if not already impersonating and we have a current courier
      if (!state.isImpersonating && state.courier) {
        // Store the original courier for restoration later
        state.originalCourier = state.courier;
        state.impersonatedCourier = action.payload;
        state.isImpersonating = true;
        
        // Set the main courier to the impersonated one for seamless integration
        // This ensures all existing code that reads state.courier works correctly
        state.courier = action.payload;
      }
    },
    stopImpersonation: (state) => {
      // Only allow stopping if currently impersonating and we have an original courier
      if (state.isImpersonating && state.originalCourier) {
        // Restore the original courier
        state.courier = state.originalCourier;
        
        // Clear impersonation state
        state.isImpersonating = false;
        state.originalCourier = undefined;
        state.impersonatedCourier = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCourier.pending, (state) => {
      // Keep any courier we already have: a refetch must not look like a cold
      // start, or App swaps the router for the full-screen loader (see
      // utils/appBootstrapView.ts).
      state.isLoading = true;
      state.error = undefined;
    });
    builder.addCase(fetchCourier.fulfilled, (state, action) => {
      // console.log('action.payload', action.payload);

      // Resolved alongside the courier so AdminProtectedRoute and the sidebar
      // never have to race an in-page fetch. Impersonation overwrites it below.
      state.isPartnerLinked = !!(action.payload as { isPartnerLinked?: boolean })?.isPartnerLinked;
      state.partnerLinkResolved = !!(action.payload as { partnerLinkResolved?: boolean })?.partnerLinkResolved;
      
      // During impersonation, preserve the impersonated courier state
      // and store the fetched courier as the original courier if not already set
      if (state.isImpersonating) {
        // If we don't have an original courier stored, store the fetched one
        if (!state.originalCourier) {
          state.originalCourier = action.payload;
        }
        // Don't update the main courier state during impersonation
        // The effective courier remains the impersonated one
      } else {
        // Normal behavior: set the fetched courier as the current courier
        state.courier = action.payload;
      }

      // Always use the effective courier for UI elements
      const effectiveCourier = state.isImpersonating ? state.impersonatedCourier : state.courier;
      const courierName = effectiveCourier?.name || 'OX Courier Admin';
      const titleText = `${courierName} | Admin`;
      
      // Update document title
      document.title = titleText;

      // Update meta description
      const descriptionText = `${courierName} - Admin Dashboard for managing your courier business`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', descriptionText);
      }

      // Update theme color
      const themeColor = '#6366f1'; // Indigo theme
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', themeColor);
      }

      // Update apple mobile web app title
      const appleMobileTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (appleMobileTitle) {
        appleMobileTitle.setAttribute('content', courierName);
      }

      // Update favicon. Point EVERY <link rel="icon"> at the courier logo — the
      // static index.html ships several sized OX icon links, and if we only update
      // the first one the browser keeps showing an OX icon in the tab. Strip the
      // sizes/type hints so the courier image is used regardless of declared size.
      const faviconUrl = effectiveCourier?.imageUrl ||
        'https://ik.imagekit.io/47zhisylvftkf/ox_logo_only_IFYxphj5k?updatedAt=1661912145854';
      const faviconLinks = document.querySelectorAll('link[rel="icon"]');
      if (faviconLinks.length > 0) {
        faviconLinks.forEach((favicon) => {
          favicon.setAttribute('href', faviconUrl);
          favicon.removeAttribute('sizes');
          favicon.removeAttribute('type');
        });
      } else {
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = faviconUrl;
        document.head.appendChild(favicon);
      }

      // The courier logo is often a WebP, which Chrome doesn't reliably render as
      // a tab favicon (it falls back to the OX favicon.ico). Render a PNG copy of
      // the logo and point every icon link at it once it's ready.
      if (effectiveCourier?.imageUrl) {
        generateFaviconPng(effectiveCourier.imageUrl).then((faviconPng) => {
          if (!faviconPng) return;
          document.querySelectorAll('link[rel="icon"]').forEach((favicon) => {
            favicon.setAttribute('href', faviconPng);
            favicon.removeAttribute('sizes');
            favicon.removeAttribute('type');
          });
        });
      }

      // Update apple touch icons
      const appleTouchIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
      appleTouchIcons.forEach(icon => {
        if (effectiveCourier?.imageUrl) {
          icon.setAttribute('href', effectiveCourier.imageUrl);
        }
      });

      // Dynamically update the PWA manifest with courier-specific branding so the
      // installed app uses the courier's logo and name. We update the single
      // existing <link rel="manifest"> (VitePWA's static baseline) in place — a
      // second link would be shadowed, since browsers use the first manifest link.
      try {
        const manifest = buildCourierManifest({
          courierName,
          themeColor,
          imageUrl: effectiveCourier?.imageUrl,
          origin: window.location.origin,
        });

        const manifestBlob = new Blob([JSON.stringify(manifest)], {
          type: 'application/manifest+json',
        });
        const manifestURL = URL.createObjectURL(manifestBlob);

        const existingManifest = document.querySelector('link[rel="manifest"]');
        if (existingManifest) {
          existingManifest.setAttribute('href', manifestURL);
        } else {
          const link = document.createElement('link');
          link.rel = 'manifest';
          link.href = manifestURL;
          document.head.appendChild(link);
        }

        // Android prefers a maskable icon for the home-screen (adaptive) icon. The
        // raw courier logo is only purpose:'any', so without a courier maskable
        // icon Android picks an OX maskable fallback. Generate a padded maskable
        // version of the courier logo and re-swap the manifest once it's ready.
        if (effectiveCourier?.imageUrl) {
          const logoUrl = effectiveCourier.imageUrl;
          generateMaskableIcon(logoUrl).then((maskableIcon) => {
            if (!maskableIcon) return;
            try {
              const upgraded = buildCourierManifest({
                courierName,
                themeColor,
                imageUrl: logoUrl,
                origin: window.location.origin,
                maskableIcon,
              });
              const upgradedURL = URL.createObjectURL(
                new Blob([JSON.stringify(upgraded)], { type: 'application/manifest+json' })
              );
              const link = document.querySelector('link[rel="manifest"]');
              if (link) link.setAttribute('href', upgradedURL);
            } catch (err) {
              console.error('[PWA] Failed to apply maskable courier icon:', err);
            }
          });
        }
      } catch (e) {
        console.error('[PWA] Failed to update manifest:', e);
      }

      state.isLoading = false;
      state.error = undefined;
    });
    builder.addCase(fetchCourier.rejected, (state, action) => {
      // A failed *refresh* keeps the courier we already have; only a failed
      // first load leaves the app without one (and shows the error screen).
      if (!state.courier) {
        state.isPartnerLinked = false;
      }
      state.isLoading = false;
      state.error = action?.error?.message;
    });
    builder.addCase(refreshIsPartnerLinked.fulfilled, (state, action) => {
      state.isPartnerLinked = action.payload;
      state.partnerLinkResolved = true;
    });
    builder.addCase(fetchCourierStoreLocations.fulfilled, (state, action) => {
      state.storeLocations = action.payload;
    });
    builder.addCase(fetchCourierForImpersonation.fulfilled, (state, action) => {
      // The impersonated courier has its own partnerships — reading the
      // overlord's would show or hide Warehouse Management for the wrong tenant.
      state.isPartnerLinked = !!(action.payload as { isPartnerLinked?: boolean })?.isPartnerLinked;

      // Only start impersonation if not already impersonating and we have a current courier
      if (!state.isImpersonating && state.courier) {
        // Store the original courier for restoration later
        state.originalCourier = state.courier;
        state.impersonatedCourier = action.payload;
        state.isImpersonating = true;
        
        // Set the main courier to the impersonated one for seamless integration
        // This ensures all existing code that reads state.courier works correctly
        state.courier = action.payload;
      }
    });
  },
  selectors: {
    getIsCourierLoading: (state: CourierState) => state.isLoading,
    getIsPartnerLinked: (state: CourierState) => state.isPartnerLinked,
    getPartnerLinkResolved: (state: CourierState) => state.partnerLinkResolved,
    getCourier: (state: CourierState) => state.courier,
    getCourierId: (state: CourierState) => state.courier?.id,
    getCourierName: (state: CourierState) => state.courier?.name,
    getCourierLogo: (state: CourierState) => state.courier?.imageUrl,
    getCourierRealm: (state: CourierState) => state.courier?.realm,
    getCourierSetting: (state: CourierState) => state.courier?.courierSetting,
    // fetchCourier already includes the storeLocations relation on the courier
    // (and impersonation swaps state.courier), so fall back to it — nothing
    // dispatches fetchCourierStoreLocations, leaving state.storeLocations
    // empty in normal use. An explicitly updated list still wins.
    getStoreLocations: (state: CourierState) =>
      state.storeLocations.length > 0
        ? state.storeLocations
        : state.courier?.storeLocations ?? [],
    getCourierWarehouseLocations: (state: CourierState) =>
      state.courier?.courierSetting?.additionalShippingAddresses,
    getCourierPrefix: (state: CourierState) =>
      state.courier?.courierSetting?.prefix,
    getCourierError: (state: CourierState) => state.error,
    getHasUnpaidBills: (state: CourierState) =>
      state.courier?.courierBills?.some((bill) => bill.status === 'Unpaid'),
    getThemeSettings: (state: CourierState) => state.courier?.themeSetting,
    getWipayCredentials: (state: CourierState) => state.courier?.wipayCredentials,
    getStripeCredentials: (state: CourierState) => state.courier?.stripeCredentials,
    getOnboardingStatus: (state: CourierState) => state.courier?.onboardingStatus,
    // Impersonation selectors
    getIsImpersonating: (state: CourierState) => state.isImpersonating,
    getOriginalCourier: (state: CourierState) => state.originalCourier,
    getImpersonatedCourier: (state: CourierState) => state.impersonatedCourier,
    getEffectiveCourier: (state: CourierState) =>
      state.isImpersonating ? state.impersonatedCourier : state.courier,
    // Timezone — falls back to America/Jamaica (no DST, UTC-5) so all
    // existing Jamaica-only couriers get the correct default without a
    // migration. Miami/New York couriers set timezone: 'America/New_York'.
    getCourierTimezone: (state: CourierState): string =>
      state.courier?.courierSetting?.timezone ?? 'America/Jamaica',
    // Currency — the ISO-4217 code the courier operates in. Falls back to the
    // legacy useJmd toggle (true -> JMD, else USD) so existing couriers render
    // exactly as before without a data migration.
    getCurrency: (state: CourierState): string =>
      state.courier?.courierSetting?.currency ??
      (state.courier?.courierSetting?.useJmd ? 'JMD' : 'USD'),
  },
});

export const {
  updateCourier,
  setIsLoading,
  setIsPartnerLinked,
  updateStoreLocations,
  updateCourierSettings,
  updateThemeSettings,
  updateLegalDocument,
  startImpersonation,
  stopImpersonation,
} = courierSlice.actions;

export const {
  getIsCourierLoading,
  getIsPartnerLinked,
  getPartnerLinkResolved,
  getCourier,
  getCourierId,
  getCourierName,
  getCourierLogo,
  getCourierRealm,
  getCourierSetting,
  getStoreLocations,
  getCourierWarehouseLocations,
  getCourierPrefix,
  getCourierError,
  getHasUnpaidBills,
  getThemeSettings,
  getWipayCredentials,
  getStripeCredentials,
  getOnboardingStatus,
  getIsImpersonating,
  getOriginalCourier,
  getImpersonatedCourier,
  getEffectiveCourier,
  getCourierTimezone,
  getCurrency,
} = courierSlice.selectors;

// Note: getCourierSetting is now provided by the built-in slice selectors above

export const getUnpaidBills = createSelector(
  [(state: RootState) => state.courier.courier?.courierBills],
  (bills) => bills?.filter((bill) => bill.status === 'Unpaid') || []
);

// Memoized impersonation selectors for consistent performance
export const getEffectiveCourierMemoized = createSelector(
  [
    (state: RootState) => state.courier.isImpersonating,
    (state: RootState) => state.courier.impersonatedCourier,
    (state: RootState) => state.courier.courier
  ],
  (isImpersonating, impersonatedCourier, regularCourier) => 
    isImpersonating ? impersonatedCourier : regularCourier
);

export const getImpersonationStatus = createSelector(
  [
    (state: RootState) => state.courier.isImpersonating,
    (state: RootState) => state.courier.originalCourier,
    (state: RootState) => state.courier.impersonatedCourier
  ],
  (isImpersonating, originalCourier, impersonatedCourier) => ({
    isImpersonating,
    originalCourier,
    impersonatedCourier
  })
);

export default courierSlice.reducer;
